import React, { useState, useRef } from "react";
import Footer from "../common/Footer";
import Navigation from "../common/Navigation";
import Sidebar from "../common/Sidebar";
import CommonService from "../../services/CommonService";
import Swal from "sweetalert2";
import FormDatePicker from "../common/FormDatePicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import FormInput from "../common/FormInput";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";

const Pan = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [panDetails, setPanDetails] = useState(null);
  const formRef = useRef(null);
  const pickerRef = useRef(null);

  // Yup validation schema
  const schema = Yup.object({
    pan: Yup.string()
      .required("PAN number is required")
      .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN format"),
    name: Yup.string()
      .required("Name is required")
      .matches(/^[A-Z\s]+$/, "Only uppercase letters and spaces allowed"),
    dob: Yup.date()
      .required("Date of birth is required")
      .max(new Date(), "Date cannot be in the future"),
  });


  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onTouched",
  });

  // Handle form reset
  const handleCancel = () => {
    reset();
    setPanDetails(null);
  };

  // Submit form
  const onSubmit = async (data) => {
    setIsLoading(true);
    const formattedDob = data.dob ? format(data.dob, "yyyy-MM-dd") : "";
    const payload = {
      pan_number: data.pan.toUpperCase(),
      name: data.name.toUpperCase(),
      dob: formattedDob,
    };

    try {
      const response = await CommonService.panSearchNormal(payload);
      if (response.data.status === 200) {
        setPanDetails(response.data.data);
      } else {
        Swal.fire({
          icon: "error",
          title: response.data.status,
          text: response.data.message,
        });
        setPanDetails(null);
      }
    } catch (error) {
      console.log("An error occurred:", error?.response?.data);
      Swal.fire({
        icon: "error",
        title: error?.response?.data?.status || "Error",
        text: error?.response?.data?.message || "Something went wrong",
      });
      setPanDetails(null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Sidebar />
          <div className="layout-page">
            <Navigation />
            <div className="content-wrapper">
              <div className="container-xxl flex-grow-1 container-p-y">
                <div className="row">
                  <div className="col-md-12">
                    <div className="card h-100">
                      <div className="card-body">
                        <div className="row">
                          <h5 className="card-header">PAN Normal Report</h5>
                          <hr className="hr-line" />
                        </div>

                        {isLoading ? (
                          <div className="d-flex justify-content-center">
                            <div className="sk-fold sk-primary">
                              <div className="sk-fold-cube"></div>
                              <div className="sk-fold-cube"></div>
                              <div className="sk-fold-cube"></div>
                              <div className="sk-fold-cube"></div>
                            </div>
                          </div>
                        ) : (
                          <form
                            className="card-body"
                            ref={formRef}
                            style={{ paddingTop: "0px", paddingLeft: "0px" }}
                            onSubmit={handleSubmit(onSubmit)}
                          >
                            <div className="row">
                              <div className="col-12 col-md-4 mb-3 mb-md-0">
                                <FormInput
                                  label="PAN"
                                  name="pan"
                                  register={register}
                                  errors={errors}
                                  placeholder="Enter PAN number"
                                  maxLength={10}
                                  onInput={(e) =>
                                    (e.target.value = e.target.value.toUpperCase())
                                  }
                                />
                              </div>

                              <div className="col-12 col-md-4 mb-3 mb-md-0">
                                <FormInput
                                  label="Name"
                                  name="name"
                                  register={register}
                                  errors={errors}
                                  placeholder="Enter Name"
                                  maxLength={100}
                                  onInput={(e) =>
                                    (e.target.value = e.target.value.toUpperCase())
                                  }
                                />
                              </div>

                              <div className="col-12 col-md-4 mb-3 mb-md-0">
                                <FormDatePicker
                                  label="Dob"
                                  name="dob"
                                  setValue={setValue}
                                  watch={watch}
                                  errors={errors}
                                />
                              </div>
                            </div>

                            <div className="pt-4">
                              <button
                                type="submit"
                                className="btn btn-primary me-sm-3 me-1"
                              >
                                Find Status
                              </button>
                              <button
                                type="button"
                                className="btn btn-label-secondary"
                                onClick={handleCancel}
                              >
                                Reset
                              </button>
                            </div>
                          </form>
                        )}

                        {panDetails && (
                          <div className="row mt-4">
                            {/* Left column - PAN details */}
                            <div className="col-md-7">
                              <ul className="nav nav-pills flex-column flex-sm-row mb-4">
                                <p className="nav-link active" style={{ fontSize: "19px" }}>
                                  <i className="ti-xs ti ti-user-check me-1"></i>
                                  PAN Details
                                </p>
                              </ul>
                              <div className="card-body" style={{ paddingTop: "0px" }}>
                                {[
                                  ["PAN Number", panDetails.pan_number],
                                  ["Pan Holder Title", panDetails.pan_holder_title],
                                  ["First Name", panDetails.first_name],
                                  ["Middle Name", panDetails.middle_name],
                                  ["Last Name", panDetails.last_name],
                                  ["Date of Birth", panDetails.dob],
                                  ["Name Match", panDetails.name_match],
                                  ["DOB Match", panDetails.dob_match],
                                  ["Aadhaar Seeding Status", panDetails.aadhaar_seeding_status_desc],
                                  ["Name On Card", panDetails.name_on_card],
                                  [
                                    "Pan Status",
                                    panDetails.pan_status,
                                    panDetails.pan_status === "VALID" ? "green" : "red",
                                  ],
                                  ["Seeding Status", panDetails.seeding_status],
                                  ["Last Updated", panDetails.pan_last_updated],
                                ].map(([label, value, color], idx) => (
                                  <div
                                    key={idx}
                                    className={`row ${idx % 2 !== 0 ? "bg-light" : ""}`}
                                  >
                                    <div className="col-6 col-md-6">
                                      <b>{label}</b>
                                    </div>
                                    <div
                                      className="col-6 col-md-6 text-end"
                                      style={{ color: color || "inherit" }}
                                    >
                                      {value || "NA"}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Right column - API health check */}
                            <div className="col-md-5">
                              <ul className="nav nav-pills flex-column flex-sm-row mb-4">
                                <p className="nav-link active" style={{ fontSize: "19px" }}>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="icon icon-tabler icons-tabler-outline icon-tabler-clock-12"
                                  >
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                    <path d="M3 12a9 9 0 0 0 9 9m9 -9a9 9 0 1 0 -18 0" />
                                    <path d="M12 7v5l.5 .5" />
                                    <path d="M18 15h2a1 1 0 0 1 1 1v1a1 1 0 0 1 -1 1h-1a1 1 0 0 0 -1 1v1a1 1 0 0 0 1 1h2" />
                                    <path d="M15 21v-6" />
                                  </svg>
                                  &nbsp; API HEALTH CHECK
                                </p>
                              </ul>
                              <div className="card-body" style={{ paddingTop: "0px" }}>
                                {[
                                  ["Request Time", panDetails.request_timestamp],
                                  ["Response Time", panDetails.response_timestamp],
                                  ["Latency (sec)", panDetails.total_time],
                                ].map(([label, value], idx) => (
                                  <div
                                    key={idx}
                                    className={`row ${idx % 2 !== 0 ? "bg-light" : ""}`}
                                  >
                                    <div className="col-6 col-md-6">
                                      <b>{label}</b>
                                    </div>
                                    <div className="col-6 col-md-6 text-end">
                                      {value || "NA"}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Footer />
              <div className="content-backdrop fade"></div>
            </div>
          </div>
        </div>

        <div className="layout-overlay layout-menu-toggle"></div>
        <div className="drag-target"></div>
      </div>
    </>
  );
};

export default Pan;